import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { LogoMark } from '@/components/Logo'
import { ROUTES } from '@/routes/routes'

/**
 * Shared shell for the Privacy Policy and Terms & Conditions pages. Content mirrors
 * docs/legal/*-draft.md — keep the two in sync.
 */
export function LegalPageLayout({
  title,
  effectiveDate,
  children,
}: {
  title: string
  effectiveDate: string
  children: ReactNode
}) {
  return (
    <div className="fl-force-light min-h-screen">
      <nav className="mx-auto flex max-w-[760px] items-center justify-between px-6 pt-7">
        <Link to="/" className="flex items-center gap-2.5">
          <LogoMark size="sm" />
          <span
            className="text-xl font-bold tracking-tight text-[var(--fl-heading)]"
            style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
          >
            LogMySpends
          </span>
        </Link>
        <div className="flex items-center gap-5 text-sm font-semibold text-[var(--fl-text-2)]">
          <Link to={ROUTES.LEGAL.PRIVACY} className="hover:text-[var(--fl-primary)]">
            Privacy
          </Link>
          <Link to={ROUTES.LEGAL.TERMS} className="hover:text-[var(--fl-primary)]">
            Terms
          </Link>
        </div>
      </nav>

      <main className="mx-auto max-w-[760px] px-6 pb-24 pt-16">
        <h1
          className="text-[clamp(30px,5vw,42px)] font-bold leading-tight tracking-tight text-[var(--fl-heading)]"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          {title}
        </h1>
        <p className="mt-2 text-sm text-[var(--fl-text-3)]">Effective date: {effectiveDate}</p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-[var(--fl-text-2)]">
          {children}
        </div>
      </main>
    </div>
  )
}

export function LegalSection({
  number,
  title,
  children,
}: {
  number: number
  title: string
  children: ReactNode
}) {
  return (
    <section>
      <h2 className="mb-3 text-lg font-bold text-[var(--fl-heading)]">
        {number}. {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

export function LegalList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

export function Placeholder({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-[var(--fl-warning-bg)] px-1 py-0.5 font-mono text-[13px] text-[var(--fl-warning-text)]">
      [{children}]
    </span>
  )
}

export function MailLink({ address }: { address: string }) {
  return (
    <a href={`mailto:${address}`} className="font-medium text-[var(--fl-primary)] hover:underline">
      {address}
    </a>
  )
}
